import { ExclamationCircleFilled, SearchOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Image, Spin, Tag, Tooltip } from "antd";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { BsShieldFillCheck, BsShieldFillExclamation } from "react-icons/bs";
import useCarPricingMode from "./hooks/useCarPricingMode";
import apiClient from "../../helpers/apiClient";
import { DataTable, useTableFilters, RowActionsMenu, TruncatedText, CopyButton } from "../../components/data-table";
import FeaturedToggle from "./components/FeaturedToggle";
import PhotoIcon from "../../assets/icons/photo.svg";

const RATE_TIER_KEYS = [
    { key: "hourlyRate", titleKey: "hourly" },
    { key: "dailyRate", titleKey: "daily" },
    { key: "weeklyRate", titleKey: "weekly" },
    { key: "monthlyRate", titleKey: "monthly" },
];

// Sum of leaf column widths — required for fixed action column + header/body alignment
const CAR_TABLE_SCROLL_X = 1610;

const hasActivePricingRate = (source) =>
    RATE_TIER_KEYS.some(({ key }) => {
        const tier = source?.[key];
        return tier && !tier.disabled && tier.rate;
    });

const formatCarLocation = (car) => {
    const loc = car?.location;
    if (loc?.city && loc?.state) return `${loc.city}, ${loc.state}`;
    if (loc?.city) return loc.city;
    if (loc?.state) return loc.state;
    if (loc?.address) return loc.address;
    if (car?.baseLocation) return car.baseLocation;
    return null;
};

const INSURANCE_ACTIVE_FILTER_KEY = "insuranceVehicleInfo.active";
const PRICING_ACTIVE_FILTER_KEY = "hasActivePricing";
const LOCATION_FILTER_KEY = "location";
const FEATURED_FILTER_KEY = "isFeatured";
const ACTIVE_ICON_COLOR = "#5D5DF6";

const getLocationObjectId = (loc) => String(loc?.id ?? loc?._id ?? loc?.locationId ?? "");

const getLocationSearchLabel = (loc) => [loc?.address, loc?.city, loc?.state].filter(Boolean).join(", ");

function InsuranceShield({ enabled }) {
    return enabled ? (
        <BsShieldFillCheck
            style={{ color: "#22c55e", fontSize: "0.9rem", flexShrink: 0 }}
            title="Insurance enabled"
        />
    ) : (
        <BsShieldFillExclamation
            style={{ color: "#f59e0b", fontSize: "0.9rem", flexShrink: 0 }}
            title="Insurance not enabled"
        />
    );
}

InsuranceShield.propTypes = { enabled: PropTypes.bool };

const mapLocationToOption = (loc) => {
    const searchLabel = getLocationSearchLabel(loc);
    return {
        value: getLocationObjectId(loc),
        searchLabel,
        label: (
            <div className="flex items-start gap-2 py-0.5">
                <span className="mt-0.5">
                    <InsuranceShield enabled={Boolean(loc?.isInsuranceEnabled)} />
                </span>
                <div className="flex min-w-0 flex-col text-sm leading-snug">
                    {loc?.address ? <span>{loc.address}</span> : null}
                    <span className={loc?.address ? "text-xs text-[#9b9ba5]" : undefined}>
                        {[loc?.city, loc?.state].filter(Boolean).join(", ") || searchLabel}
                    </span>
                </div>
            </div>
        ),
    };
};

function CarList({
    datas,
    loading,
    handleDeleteCar,
    searchParams,
    onSearchParamsChange,
    t,
    title,
    count,
    headerExtra,
    pagination,
    onCarUpdated,
}) {
    const history = useHistory();
    const locationSearchTimeoutRef = useRef(null);
    const isCarPricingMode = useCarPricingMode();
    const [locationOptions, setLocationOptions] = useState([]);
    const [locationSearchLoading, setLocationSearchLoading] = useState(false);
    const [locationFilterInput, setLocationFilterInput] = useState("");
    const [selectedLocationLabel, setSelectedLocationLabel] = useState("");
    const [pendingLocationId, setPendingLocationId] = useState(null);

    const table = useTableFilters({ value: searchParams, onChange: onSearchParamsChange });

    useEffect(
        () => () => {
            if (locationSearchTimeoutRef.current) {
                clearTimeout(locationSearchTimeoutRef.current);
            }
        },
        [],
    );

    const fetchLocationSuggestions = useCallback(async (searchTerm) => {
        const trimmed = searchTerm?.trim();
        if (!trimmed) {
            setLocationOptions([]);
            return;
        }

        setLocationSearchLoading(true);
        try {
            const queryParams = new URLSearchParams({
                searchTerm: trimmed,
                limit: "20",
                page: "1",
            });
            const response = await apiClient.get(`/api/location/list?${queryParams}`);
            if (response?.data?.success) {
                const locations = response?.data?.data || [];
                setLocationOptions(locations.map(mapLocationToOption));
            } else {
                setLocationOptions([]);
            }
        } catch (_error) {
            setLocationOptions([]);
        } finally {
            setLocationSearchLoading(false);
        }
    }, []);

    const handleLocationInputSearch = useCallback(
        (text) => {
            setLocationFilterInput(text);
            if (text !== selectedLocationLabel) {
                setPendingLocationId(null);
            }
            if (locationSearchTimeoutRef.current) {
                clearTimeout(locationSearchTimeoutRef.current);
            }
            locationSearchTimeoutRef.current = setTimeout(() => {
                fetchLocationSuggestions(text);
            }, 300);
        },
        [fetchLocationSuggestions, selectedLocationLabel],
    );

    const handleLocationSelect = useCallback((locationId, option) => {
        const label = option?.searchLabel || locationId;
        setPendingLocationId(locationId);
        setSelectedLocationLabel(label);
        setLocationFilterInput(label);
    }, []);

    const handleLocationApplySearch = (confirm) => {
        if (!pendingLocationId) {
            return;
        }
        table.setFilter(LOCATION_FILTER_KEY, pendingLocationId);
        confirm();
    };

    const handleLocationReset = (clearFilters, confirm) => {
        clearFilters?.();
        table.clearFilter(LOCATION_FILTER_KEY);
        setLocationFilterInput("");
        setSelectedLocationLabel("");
        setPendingLocationId(null);
        setLocationOptions([]);
        confirm();
    };

    // car.category is populated by the API — read rates directly from the object
    const getPricingSource = (car) =>
        isCarPricingMode ? car?.rate : typeof car?.category === "object" ? car.category : null;

    const renderRateCell = (tier) => {
        if (!tier || tier.disabled) {
            return <span className="text-gray-400">{t("disabled")}</span>;
        }
        return <span className="tabular-nums">${tier.rate}</span>;
    };

    const getColumnLocationFilterProps = () => ({
        filterKey: LOCATION_FILTER_KEY,
        filteredValue: searchParams[LOCATION_FILTER_KEY] ? [searchParams[LOCATION_FILTER_KEY]] : null,
        filterDropdown: ({ confirm, clearFilters }) => (
            <div className="flex w-72 flex-col gap-2 p-2" onKeyDown={(e) => e.stopPropagation()}>
                <AutoComplete
                    value={locationFilterInput}
                    options={locationOptions}
                    onSearch={handleLocationInputSearch}
                    onSelect={handleLocationSelect}
                    placeholder={t("search")}
                    style={{ width: "100%" }}
                    notFoundContent={
                        locationSearchLoading ? (
                            <div className="flex justify-center py-2">
                                <Spin size="small" />
                            </div>
                        ) : undefined
                    }
                />
                <div className="flex items-center justify-between">
                    <Button
                        size="small"
                        type="link"
                        className="!px-0"
                        onClick={() => handleLocationReset(clearFilters, confirm)}>
                        {t("reset")}
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => handleLocationApplySearch(confirm)}
                        disabled={!pendingLocationId}>
                        {t("search")}
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? ACTIVE_ICON_COLOR : undefined }} />,
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    const activeLocationId = searchParams[LOCATION_FILTER_KEY];
                    if (activeLocationId) {
                        setPendingLocationId(activeLocationId);
                        setLocationFilterInput(selectedLocationLabel || activeLocationId);
                    } else {
                        setPendingLocationId(null);
                        setLocationFilterInput("");
                        setLocationOptions([]);
                    }
                }
            },
        },
    });

    const columns = [
        {
            title: t("carImage"),
            dataIndex: ["images", "image1"],
            key: "carImage",
            width: 96,
            align: "center",
            onCell: () => ({
                style: { paddingTop: 6, paddingBottom: 6 },
            }),
            render: (image) => (
                <div className="flex h-12 w-16 items-center justify-center overflow-hidden rounded-md bg-gray-50">
                    {image ? (
                        <Image
                            src={image}
                            alt=""
                            width={64}
                            height={48}
                            className="rounded-md object-cover"
                            style={{ objectFit: "cover" }}
                        />
                    ) : (
                        <img src={PhotoIcon} alt="" className="h-6 w-6 opacity-40" />
                    )}
                </div>
            ),
        },
        {
            title: t("carName"),
            dataIndex: "carName",
            key: "carName",
            width: 230,
            ...table.getColumnSearchProps("carName"),
            render: (carName, car) => {
                const showNoRateAlert = isCarPricingMode && !hasActivePricingRate(getPricingSource(car));

                return (
                    <div className="flex min-w-0 items-center gap-2">
                        <TruncatedText>
                            <Link to={`/cardetails/${car?.VIN || ""}`} className="sc-table-link">
                                {carName || "N/A"}
                            </Link>
                        </TruncatedText>
                        {car?.isDisabled && (
                            <Tooltip title={t("disabledTagTooltip")}>
                                <Tag color="error" className="!m-0 shrink-0 rounded">
                                    {t("disabledTag")}
                                </Tag>
                            </Tooltip>
                        )}
                        {showNoRateAlert && (
                            <Tooltip title={t("missingCarPricingTooltip")}>
                                <ExclamationCircleFilled
                                    style={{ fontSize: "0.9rem", color: "#ef4444", cursor: "help", flexShrink: 0 }}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: t("vinNumberShort"),
            dataIndex: "VIN",
            key: "VIN",
            width: 200,
            ...table.getColumnSearchProps("VIN"),
            render: (vin) =>
                vin ? (
                    <span className="flex items-center gap-1.5">
                        <span className="sc-mono">{vin}</span>
                        <CopyButton value={vin} />
                    </span>
                ) : (
                    "N/A"
                ),
        },
        {
            title: t("plateNumber"),
            dataIndex: "carNumber",
            key: "carNumber",
            width: 150,
            ...table.getColumnSearchProps("carNumber"),
            render: (carNumber) =>
                carNumber ? (
                    <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs font-semibold tracking-wide text-gray-700">
                        {carNumber}
                    </span>
                ) : (
                    "N/A"
                ),
        },
        {
            title: t("rate"),
            key: "rate",
            ...(isCarPricingMode
                ? table.getColumnSelectProps(PRICING_ACTIVE_FILTER_KEY, [
                      { value: "true", label: t("withActivePricing") },
                      { value: "false", label: t("withoutActivePricing") },
                  ])
                : {}),
            children: RATE_TIER_KEYS.map(({ key, titleKey }) => ({
                title: t(titleKey),
                key,
                width: 90,
                align: "right",
                render: (_, car) => renderRateCell(getPricingSource(car)?.[key]),
            })),
        },
        {
            title: t("location"),
            dataIndex: "location",
            key: "location",
            width: 190,
            ...getColumnLocationFilterProps(),
            render: (_, car) => {
                const displayText = formatCarLocation(car);
                if (!displayText) {
                    return "N/A";
                }

                const loc = car?.location;
                return (
                    <div className="flex items-center gap-2">
                        {loc && <InsuranceShield enabled={Boolean(loc.isInsuranceEnabled)} />}
                        <TruncatedText className="text-sm">{displayText}</TruncatedText>
                    </div>
                );
            },
        },
        {
            title: t("featuredColumn"),
            dataIndex: "isFeatured",
            key: "isFeatured",
            width: 110,
            align: "center",
            ...table.getColumnSelectProps(FEATURED_FILTER_KEY, [{ value: "true", label: t("featuredFilterOnly") }]),
            render: (_, car) => <FeaturedToggle car={car} onChange={onCarUpdated} />,
        },
        {
            title: t("category"),
            dataIndex: "categoryName",
            key: "categoryName",
            width: 150,
            ellipsis: true,
            ...table.getColumnSearchProps("categoryName"),
            render: (categoryName) => categoryName || "N/A",
        },
        {
            title: t("carDetail.insurance"),
            dataIndex: "insuranceVehicleInfo",
            key: "insuranceVehicleInfo",
            width: 130,
            ...table.getColumnSelectProps(INSURANCE_ACTIVE_FILTER_KEY, [
                { value: "true", label: t("carDetail.linked") },
                { value: "false", label: t("carDetail.unlinked") },
            ]),
            render: (_, car) => {
                const isLinked = car?.insuranceVehicleInfo?.active === true;
                return (
                    <Tooltip
                        title={isLinked ? t("carDetail.insuranceLinkedActive") : t("carDetail.insuranceNotLinked")}>
                        <Tag color={isLinked ? "green" : "red"} className="!m-0">
                            {isLinked ? t("carDetail.linked") : t("carDetail.notLinked")}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: <span className="sr-only">{t("action")}</span>,
            key: "action",
            width: 64,
            align: "center",
            fixed: "right",
            render: (_, car) => {
                const actionItems = [
                    { label: t("view"), key: "view" },
                    { label: t("rates.update"), key: "update" },
                    { label: t("delete"), key: "delete", danger: true },
                ];

                const onClickAction = ({ key }) => {
                    if (key === "view") {
                        history.push(`/cardetails/${car?.VIN || ""}`);
                        return;
                    }
                    if (key === "update") {
                        history.push(`/cardetails/${car?.VIN || ""}`, { edit: true });
                        return;
                    }
                    if (key === "delete") {
                        handleDeleteCar(car);
                    }
                };

                return (
                    <RowActionsMenu items={actionItems} onClick={onClickAction} label={t("action")} />
                );
            },
        },
    ];

    const dataSource = useMemo(() => {
        const mapped = datas.map((car, index) => ({
            ...car,
            key: car.id || index,
        }));

        const pricingFilter = searchParams[PRICING_ACTIVE_FILTER_KEY];
        if (!isCarPricingMode || !pricingFilter) {
            return mapped;
        }

        return mapped.filter((car) => {
            const hasRate = hasActivePricingRate(car?.rate);
            if (pricingFilter === "true") return hasRate;
            if (pricingFilter === "false") return !hasRate;
            return true;
        });
    }, [datas, isCarPricingMode, searchParams]);

    return (
        <DataTable
            title={title}
            count={count}
            headerExtra={headerExtra}
            filters={table}
            search={{
                fields: [
                    { key: "carName", label: t("carName") },
                    { key: "VIN", label: t("vinNumberShort") },
                    { key: "carNumber", label: t("plateNumber") },
                    { key: "categoryName", label: t("category") },
                ],
            }}
            filterLabels={{
                [LOCATION_FILTER_KEY]: t("location"),
                [PRICING_ACTIVE_FILTER_KEY]: t("rate"),
                [FEATURED_FILTER_KEY]: t("featuredColumn"),
                [INSURANCE_ACTIVE_FILTER_KEY]: t("carDetail.insurance"),
            }}
            formatFilterValue={(key) => (key === LOCATION_FILTER_KEY ? selectedLocationLabel || undefined : undefined)}
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            rowKey="id"
            scroll={{ x: CAR_TABLE_SCROLL_X }}
            pagination={pagination}
        />
    );
}

CarList.propTypes = {
    datas: PropTypes.arrayOf(PropTypes.object).isRequired,
    loading: PropTypes.bool.isRequired,
    handleDeleteCar: PropTypes.func.isRequired,
    searchParams: PropTypes.object.isRequired,
    onSearchParamsChange: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    title: PropTypes.node,
    count: PropTypes.number,
    headerExtra: PropTypes.node,
    pagination: PropTypes.object,
    onCarUpdated: PropTypes.func,
};

export default CarList;
